import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth, getToken } from "@/lib/authContext";
import { ItemWishlist, WishlistItem } from "@/components/ItemWishlist";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2, UserPlus, UserMinus, MessageCircle } from "lucide-react";
import { useRoute, useLocation } from "wouter";
import { useI18n } from "@/lib/i18n";
import { useToast } from "@/hooks/use-toast";

interface SharedWishlistData {
    owner: {
        id: string;
        username: string;
        avatarUrl: string | null;
        isInsider: boolean;
    };
    items: WishlistItem[];
    isFollowing: boolean;
}

export default function WishlistSharedPage() {
    const { t } = useI18n();
    const { toast } = useToast();
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const [_, setLocation] = useLocation();
    const [match, params] = useRoute("/wishlist/shared/:token");

    const token = params?.token || "";

    const { data, isLoading, error } = useQuery({
        queryKey: ["shared-wishlist", token],
        queryFn: async () => {
            const headers: Record<string, string> = {};
            if (user) {
                const authToken = await getToken();
                if (authToken) headers.Authorization = `Bearer ${authToken}`;
            }
            const res = await fetch(`/api/wishlist/shared/${token}`, { headers });
            if (!res.ok) throw new Error("Not found");
            return res.json() as Promise<SharedWishlistData>;
        },
        enabled: !!token,
    });

    const followMutation = useMutation({
        mutationFn: async () => {
            if (!data?.owner?.id) return;
            const authToken = await getToken();
            const res = await fetch(`/api/wishlist/follow/${data.owner.id}`, {
                method: "POST",
                headers: { Authorization: `Bearer ${authToken}` },
            });
            if (!res.ok) throw new Error("Follow failed");
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["shared-wishlist", token] });
            queryClient.invalidateQueries({ queryKey: ["wishlist-following"] });
            toast({ title: "Gevolgd!", duration: 2000 });
        },
    });

    const unfollowMutation = useMutation({
        mutationFn: async () => {
            if (!data?.owner?.id) return;
            const authToken = await getToken();
            const res = await fetch(`/api/wishlist/follow/${data.owner.id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${authToken}` },
            });
            if (!res.ok) throw new Error("Unfollow failed");
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["shared-wishlist", token] });
            queryClient.invalidateQueries({ queryKey: ["wishlist-following"] });
        },
    });

    const handleFoundForFriend = (item: WishlistItem) => {
        if (!data?.owner) return;
        const message = encodeURIComponent(`Hoi ${data.owner.username}, ik heb de ${item.item_name} voor je gevonden!`);
        window.open(`https://wa.me/?text=${message}`, "_blank");
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4">
                <p className="text-muted-foreground">{t("wishlist.notFound")}</p>
                <Button onClick={() => setLocation("/wishlist")}>{t("wishlist.goToOwn")}</Button>
            </div>
        );
    }

    const isOwnList = user?.id === data.owner.id;

    return (
        <div className="min-h-screen bg-background flex flex-col pb-20 md:pb-0">
            <main className="flex-1 container mx-auto px-4 py-8 max-w-2xl">
                <Card>
                    <CardHeader>
                        <div className="flex flex-row items-center justify-between gap-4">
                            <div className="flex items-center gap-4 min-w-0">
                                <Avatar className="h-10 w-10 sm:h-14 sm:w-14 shrink-0">
                                    <AvatarImage src={data.owner.avatarUrl || undefined} />
                                    <AvatarFallback className="text-lg">{(data.owner.username || "?").substring(0, 2).toUpperCase()}</AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                    <CardTitle className="text-lg sm:text-xl truncate">{t("wishlist.sharedTitle", { name: data.owner.username })}</CardTitle>
                                    <CardDescription className="truncate">{t("wishlist.helpSearch", { name: data.owner.username })}</CardDescription>
                                </div>
                            </div>

                            {user && !isOwnList && (
                                <div className="shrink-0">
                                    {data.isFollowing ? (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => unfollowMutation.mutate()}
                                            disabled={unfollowMutation.isPending}
                                            className="h-8 sm:h-9 text-xs sm:text-sm gap-1.5"
                                        >
                                            <UserMinus className="h-3.5 w-3.5" />
                                            {t("wishlist.unfollow")}
                                        </Button>
                                    ) : (
                                        <Button
                                            size="sm"
                                            onClick={() => followMutation.mutate()}
                                            disabled={followMutation.isPending}
                                            className="h-8 sm:h-9 text-xs sm:text-sm gap-1.5"
                                        >
                                            <UserPlus className="h-3.5 w-3.5" />
                                            {t("wishlist.follow")}
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>

                        {!user && (
                            <p className="text-xs sm:text-sm text-muted-foreground mt-3">
                                {t("wishlist.loginToFollow")}
                            </p>
                        )}
                    </CardHeader>
                    <CardContent>
                        {data.items.length > 0 ? (
                            <ItemWishlist items={data.items} readonly onFoundForFriend={user && !isOwnList ? handleFoundForFriend : undefined} />
                        ) : (
                            <p className="text-muted-foreground text-center py-8">
                                {t("wishlist.userNoItems", { name: data.owner.username })}
                            </p>
                        )}
                    </CardContent>
                </Card>
            </main>
        </div>
    );
}
